/**
 * Module #34 Phase A chunk 7 ⑥ — /admin/brief full-page Daily Brief.
 *
 * A dedicated, linkable page for the proactive surveillance cards (vs. the
 * floating copilot bubble). Renders the same shared InsightBrief — here with all
 * category sections expanded — over the active tenant's open insights, with
 * one-click direct-fire + dismiss. Tier-3 actions (which need a typed challenge
 * phrase) route through the copilot chat; a link to /admin/copilot is provided.
 */

import { Link } from 'react-router-dom';
import { DigestSettings } from '../../components/copilot/DigestSettings';
import { InsightBrief } from '../../components/copilot/InsightBrief';
import { useCopilotInsights } from '../../hooks/useCopilotInsights';

export function AdminBrief() {
  const { insights, counts, status, refresh, dismiss, fireAction } = useCopilotInsights();

  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--admin-hairline, var(--border))',
        borderRadius: 'var(--r-card, 0.75rem)',
        overflow: 'hidden',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1rem',
          padding: '1.25rem 1.5rem',
          borderBottom: '1px solid var(--admin-hairline, var(--border))',
          background: 'var(--surface-elev)',
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontFamily: 'var(--font-heading-active)',
              fontSize: '1.5rem',
              fontWeight: 700,
              color: 'var(--text)',
            }}
          >
            What needs attention
            {counts.total > 0 ? ` · ${counts.total}` : ''}
          </h1>
          <div style={{ marginTop: '0.25rem', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
            Your copilot watches your tenant daily and surfaces what to act on. Fire an action
            inline, or open the{' '}
            <Link to="/admin/copilot" style={{ color: 'var(--brand-primary, #2264dc)' }}>
              copilot chat
            </Link>{' '}
            to dig in.
          </div>
        </div>
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={status.kind === 'loading'}
          style={{
            border: '1px solid var(--admin-hairline, var(--border))',
            background: 'var(--surface)',
            color: 'var(--text)',
            borderRadius: 'var(--r-tile, 0.375rem)',
            padding: '0.4rem 0.75rem',
            fontSize: '0.8125rem',
            fontWeight: 600,
            cursor: status.kind === 'loading' ? 'default' : 'pointer',
            opacity: status.kind === 'loading' ? 0.6 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          {status.kind === 'loading' ? 'Refreshing…' : 'Refresh'}
        </button>
      </header>

      <div style={{ padding: '1.25rem 1.5rem' }}>
        <DigestSettings />
        {status.kind === 'error' ? (
          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--danger, #dc2626)' }}>
            Couldn't load the brief: {status.detail}
          </p>
        ) : insights.length === 0 ? (
          <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-muted)' }}>
            {status.kind === 'loading'
              ? 'Loading…'
              : 'Nothing needs attention right now. New items appear here as your copilot finds them.'}
          </p>
        ) : (
          <InsightBrief
            insights={insights}
            onDismiss={(id) => void dismiss(id)}
            onFireAction={fireAction}
          />
        )}
      </div>
    </div>
  );
}
